import {gql} from '@apollo/client';
import {compact, groupByUniqueKey} from '@benchling/js-utils';
import {useMemo} from 'react';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {SuccessReturnType} from 'graphql-client/BenchlingQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {AllBoxSchemasWithRegistryQuery} from 'inventory/__generated/AllBoxSchemasWithRegistryQuery';
import {AllContainerSchemasWithRegistryQuery} from 'inventory/__generated/AllContainerSchemasWithRegistryQuery';
import {AllLocationSchemasWithRegistryQuery} from 'inventory/__generated/AllLocationSchemasWithRegistryQuery';
import {AllPlateSchemasWithRegistryQuery} from 'inventory/__generated/AllPlateSchemasWithRegistryQuery';
import {
  useBoxSchemasQueryWithRegistry,
  useContainerSchemasQueryWithRegistry,
  useLocationSchemasQueryWithRegistry,
  usePlateSchemasQueryWithRegistry,
} from 'inventory/useStorableSchemasQuery';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from 'lookup-config/LookupConfigUtils';
import {AllResultSchemasQuery} from 'results/__generated/AllResultSchemasQuery';
import {useAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {filterNullFields} from 'schemas/fields/filterNullFields';
import {Schema} from 'schemas/SchemaConstants';
import {AllSchemaInterfacesQuery} from 'schemas/util/__generated/AllSchemaInterfacesQuery';
import {TagSchemasWithRegistryQuery} from 'schemas/util/__generated/TagSchemasWithRegistryQuery';
import {useAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';

import {AllProcessTaskSchemasForSchemaConfigQuery} from './__generated/AllProcessTaskSchemasForSchemaConfigQuery';

export const ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY = gql`
  query AllProcessTaskSchemasForSchemaConfigQuery @skipAudit @engComponent(name: WORKFLOWS) {
    ...AllProcessTaskSchemasForSchemaConfigGraphQL
  }
  ${getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig').fragment}
`;

export function doAllProcessTaskSchemasForSchemaConfigQuery(): Promise<AllProcessTaskSchemasForSchemaConfigQuery> {
  return doQuerySafe<AllProcessTaskSchemasForSchemaConfigQuery, {}>({
    query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY,
  });
}

export const PROCESSES_TAG_SCHEMAS_INTERFACES_QUERY = gql`
  query ProcessesTagSchemasInterfacesQuery @skipAudit @engComponent(name: WORKFLOWS) {
    tagSchemas {
      id
      interfaces {
        id
      }
    }
  }
`;

interface SchemaWithFieldsJson {
  id: string;
  name: string;
  fieldsJson: JSONScalar;
  allFieldsJson: JSONScalar;
}

function doesSchemaHaveFieldsJson(schema: SchemaWithFieldsJson | Schema): schema is SchemaWithFieldsJson {
  return (schema as SchemaWithFieldsJson).fieldsJson != null;
}

export function getAllSchemasByIdFromQuery(
  tagSchemasResult: SuccessReturnType<TagSchemasWithRegistryQuery>,
  boxSchemasResult: SuccessReturnType<AllBoxSchemasWithRegistryQuery>,
  containerSchemasResult: SuccessReturnType<AllContainerSchemasWithRegistryQuery>,
  locationSchemasResult: SuccessReturnType<AllLocationSchemasWithRegistryQuery>,
  plateSchemasResult: SuccessReturnType<AllPlateSchemasWithRegistryQuery>,
  resultSchemasResult: SuccessReturnType<AllResultSchemasQuery>,
  processTaskSchemasResult: SuccessReturnType<AllProcessTaskSchemasForSchemaConfigQuery>,
  schemaInterfacesResult: SuccessReturnType<AllSchemaInterfacesQuery>
): SchemaById {
  return groupByUniqueKey(
    [
      ...tagSchemasResult.data.tagSchemas,
      ...boxSchemasResult.data.boxSchemas,
      ...containerSchemasResult.data.containerSchemas,
      ...locationSchemasResult.data.locationSchemas,
      ...plateSchemasResult.data.plateSchemas,
      ...resultSchemasResult.data.resultSchemas,
      ...processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature,
      ...compact(
        processTaskSchemasResult.data.processTaskSchemasSupportingPlatformFeature.map(
          ({outputSchema}) => outputSchema
        )
      ),
    ]
      .map((schema: SchemaWithFieldsJson | Schema): Schema =>
        doesSchemaHaveFieldsJson(schema)
          ? {
              ...schema,
              fields: filterNullFields(schema.fieldsJson),
              allFields: filterNullFields(schema.allFieldsJson),
            }
          : schema
      )
      .concat(
        schemaInterfacesResult.data.schemaInterfaces.map((schemaInterface) =>
          mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(schemaInterface)
        )
      ),
    ({id}) => id
  );
}

export function useAllSchemasById(): {
  allSchemasById: SchemaById;
  allSchemasQueryResultType: QueryResultType;
} {
  const tagSchemasResult = useTagSchemasWithRegistryQuery();
  const boxSchemasResult = useBoxSchemasQueryWithRegistry();
  const containerSchemasResult = useContainerSchemasQueryWithRegistry();
  const locationSchemasResult = useLocationSchemasQueryWithRegistry();
  const plateSchemasResult = usePlateSchemasQueryWithRegistry();
  const resultSchemasResult = useAllResultSchemasQuery();
  const processTaskSchemasResult = useBenchlingQuery<AllProcessTaskSchemasForSchemaConfigQuery>(
    ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY
  );
  const allSchemaInterfacesResult = useAllSchemaInterfacesQuery();

  const allSchemasById = useMemo(() => {
    const queryResultTypes = [
      tagSchemasResult.type,
      boxSchemasResult.type,
      containerSchemasResult.type,
      locationSchemasResult.type,
      plateSchemasResult.type,
      resultSchemasResult.type,
      processTaskSchemasResult.type,
      allSchemaInterfacesResult.type,
    ];
    const error = queryResultTypes.find((type) => type === QueryResultType.ERROR);
    const pending = queryResultTypes.find((type) => type === QueryResultType.PENDING);
    const allSchemasQueryResultType =
      error != null
        ? QueryResultType.ERROR
        : pending != null
          ? QueryResultType.PENDING
          : QueryResultType.SUCCESS;

    return {
      allSchemasById:
        tagSchemasResult.type === QueryResultType.SUCCESS &&
        boxSchemasResult.type === QueryResultType.SUCCESS &&
        containerSchemasResult.type === QueryResultType.SUCCESS &&
        locationSchemasResult.type === QueryResultType.SUCCESS &&
        plateSchemasResult.type === QueryResultType.SUCCESS &&
        resultSchemasResult.type === QueryResultType.SUCCESS &&
        processTaskSchemasResult.type === QueryResultType.SUCCESS &&
        allSchemaInterfacesResult.type === QueryResultType.SUCCESS
          ? getAllSchemasByIdFromQuery(
              tagSchemasResult,
              boxSchemasResult,
              containerSchemasResult,
              locationSchemasResult,
              plateSchemasResult,
              resultSchemasResult,
              processTaskSchemasResult,
              allSchemaInterfacesResult
            )
          : {},
      allSchemasQueryResultType,
    };
  }, [
    tagSchemasResult,
    boxSchemasResult,
    containerSchemasResult,
    locationSchemasResult,
    plateSchemasResult,
    resultSchemasResult,
    processTaskSchemasResult,
    allSchemaInterfacesResult,
  ]);
  return allSchemasById;
}
